import { Observable } from "rxjs/internal/Observable";
import { catchError, map } from "rxjs/operators";
import { API_URL } from "../../enums/api.enums";
import HttpClient from "../http-client";
import { 
    Voucher, 
    CreateVoucherDto, 
    UpdateVoucherDto, 
    UserVoucher,
    ValidateVoucherRequest,
    CalculateVoucherRequest,
    DiscountType
} from '../../common/voucher.interface';

export default class VoucherApi {
    static apiURL = API_URL;

    // Admin APIs - Quản lý Voucher

    // GET /voucher - Lấy danh sách tất cả voucher (Admin only)
    static getAll(): Observable<any> {
        const api = `${VoucherApi.apiURL.HOST}/voucher`;
        return HttpClient.get(api).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error fetching vouchers:', error);
                return new Observable();
            })
        );
    }

    // GET /voucher/active - Lấy danh sách voucher đang hoạt động
    static getActive(): Observable<any> {
        const api = `${VoucherApi.apiURL.HOST}/voucher/active`;
        return HttpClient.get(api).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error fetching active vouchers:', error);
                return new Observable();
            })
        );
    }

    // GET /voucher/exchangeable - Lấy vouchers có thể đổi bằng điểm
    static getExchangeable(): Observable<any> {
        const api = `${VoucherApi.apiURL.HOST}/voucher/exchangeable`;
        return HttpClient.get(api).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error fetching exchangeable vouchers:', error);
                return new Observable();
            })
        );
    }

    // GET /voucher/:id - Lấy voucher theo ID (Admin only)
    static getById(id: number): Observable<any> {
        const api = `${VoucherApi.apiURL.HOST}/voucher/${id}`;
        return HttpClient.get(api).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error fetching voucher by ID:', error);
                return new Observable();
            })
        );
    }

    // GET /voucher/code/:code - Lấy voucher theo mã code
    static getByCode(code: string): Observable<any> {
        const api = `${VoucherApi.apiURL.HOST}/voucher/code/${code}`;
        return HttpClient.get(api).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error fetching voucher by code:', error);
                return new Observable();
            })
        );
    }

    // POST /voucher - Tạo voucher mới (Admin only)
    static create(data: CreateVoucherDto): Observable<any> {
        const api = `${VoucherApi.apiURL.HOST}/voucher`;
        // Sử dụng CHÍNH XÁC theo API Guide để tránh lỗi 500
        const requestBody: any = {
            code: data.code,                                    // Bắt buộc
            name: data.name || data.title,                      // Bắt buộc - sử dụng name, không phải title
            description: data.description,                      // Tùy chọn
            type: data.type || (data.pointsRequired && data.pointsRequired > 0 ? 'PAID' : 'FREE'), // Bắt buộc
            discountType: data.discountType,                    // Tùy chọn, mặc định PERCENTAGE
            discount: data.discount || data.discountValue,      // Bắt buộc - sử dụng discount, không phải discountValue
            minOrder: data.minOrder || data.minOrderValue,      // Tùy chọn - sử dụng minOrder, không phài minOrderValue
            pointsCost: data.pointsCost || data.pointsRequired, // Tùy chọn - sử dụng pointsCost, không phải pointsRequired
            quantity: data.quantity || data.usageLimit,         // Bắt buộc - sử dụng quantity, không phải usageLimit
            startDate: data.startDate,                          // Bắt buộc
            endDate: data.endDate,                              // Bắt buộc
            isActive: data.isActive !== undefined ? data.isActive : true // Tùy chọn, mặc định true
        };

        // Chỉ thêm maxDiscount khi discountType là PERCENTAGE
        if (data.discountType === DiscountType.PERCENTAGE && (data.maxDiscount || data.maxDiscountAmount)) {
            requestBody.maxDiscount = data.maxDiscount || data.maxDiscountAmount;
        }

        console.log('Creating voucher:', requestBody);
        return HttpClient.post(api, requestBody).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error creating voucher:', error);
                return new Observable();
            })
        );
    }

    // PUT /voucher/:id - Cập nhật voucher (Admin only)
    static update(id: number, data: UpdateVoucherDto): Observable<any> {
        const api = `${VoucherApi.apiURL.HOST}/voucher/${id}`;
        const requestBody = {
            ...(data.title && { title: data.title }),
            ...(data.description !== undefined && { description: data.description }),
            ...(data.discountValue && { discountValue: data.discountValue }),
            ...(data.maxDiscountAmount && { maxDiscountAmount: data.maxDiscountAmount }),
            ...(data.usageLimit && { usageLimit: data.usageLimit }),
            ...(data.isActive !== undefined && { isActive: data.isActive })
        };

        console.log('Updating voucher:', requestBody);
        return HttpClient.put(api, requestBody).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error updating voucher:', error);
                return new Observable();
            })
        );
    }

    // DELETE /voucher/:id - Xóa voucher (Admin only)
    static delete(id: number): Observable<any> {
        const api = `${VoucherApi.apiURL.HOST}/voucher/${id}`;
        return HttpClient.delete(api).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error deleting voucher:', error);
                return new Observable();
            })
        );
    }

    // User APIs - Voucher Operations

    // GET /voucher/my-vouchers - Lấy danh sách voucher của user hiện tại
    static getMyVouchers(): Observable<any> {
        const api = `${VoucherApi.apiURL.HOST}/voucher/my-vouchers`;
        return HttpClient.get(api).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error fetching my vouchers:', error);
                return new Observable();
            })
        );
    }

    // POST /voucher/exchange/:voucherId - Đổi điểm để lấy voucher
    static exchange(voucherId: number): Observable<any> {
        const api = `${VoucherApi.apiURL.HOST}/voucher/exchange/${voucherId}`;
        return HttpClient.post(api, {}).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error exchanging voucher:', error);
                return new Observable();
            })
        );
    }

    // Voucher Usage APIs

    // POST /voucher/validate/:code - Kiểm tra voucher có hợp lệ không
    static validate(code: string, orderTotal: number): Observable<any> {
        const api = `${VoucherApi.apiURL.HOST}/voucher/validate/${code}`;
        const requestBody: ValidateVoucherRequest = { orderTotal };
        
        return HttpClient.post(api, requestBody).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error validating voucher:', error);
                return new Observable();
            })
        );
    }

    // POST /voucher/calculate/:code - Tính toán giá trị voucher cho đơn hàng
    static calculate(code: string, orderTotal: number): Observable<any> {
        const api = `${VoucherApi.apiURL.HOST}/voucher/calculate/${code}`;
        const requestBody: CalculateVoucherRequest = { orderTotal };
        
        return HttpClient.post(api, requestBody).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error calculating voucher:', error);
                return new Observable();
            })
        );
    }

    // POST /voucher/use/:code - Sử dụng voucher bằng code
    static use(code: string): Observable<any> {
        const api = `${VoucherApi.apiURL.HOST}/voucher/use/${code}`;
        return HttpClient.post(api, {}).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error using voucher:', error);
                return new Observable();
            })
        );
    }

    // POST /voucher/my-vouchers/:userVoucherId/use - Sử dụng voucher từ user's collection
    static useMyVoucher(userVoucherId: number): Observable<any> {
        const api = `${VoucherApi.apiURL.HOST}/voucher/my-vouchers/${userVoucherId}/use`;
        return HttpClient.post(api, {}).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error using my voucher:', error);
                return new Observable();
            })
        );
    }

    // Legacy methods for backward compatibility
    static getByType(type: string): Observable<any> {
        // Map old type to new API
        if (type === 'FREE') {
            return this.getExchangeable();
        } else {
            return this.getActive();
        }
    }
}